import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  Avatar,
  Stack,
  CircularProgress,
  Chip,
  Collapse,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useResponsive from '../hooks/useResponsive';
import {
  Analytics as AnalyticsIcon,
  TrendingUp,
  TrendingDown,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  Speed as SpeedIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { sr } from 'date-fns/locale';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import ExportAnalytics from '../components/Analytics/ExportAnalytics';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import { logger } from '../utils/logger';

interface DashboardMetric {
  title: string;
  value: number | string;
  change: number;
  isPositive: boolean;
  icon: React.ReactNode;
  color: 'primary' | 'success' | 'warning' | 'error' | 'info';
}

interface ChartData {
  date: string;
  passes: number;
  downloads: number;
  active_users: number;
  apple_wallet: number;
  google_pay: number;
}

interface MoMChange {
  total_passes: number;
  installed_passes: number;
}

interface YoYChange {
  total_passes: number;
  installed_passes: number;
}

interface AnalyticsData {
  total_passes: number;
  active_passes: number;
  total_downloads: number;
  apple_wallet: number;
  google_pay: number;
  invalidated_passes: number;
  deleted_passes: number;
  monthly_growth: number;
  daily_active_users: number;
  conversion_rate: number;
  chart_data: ChartData[];
  platform_breakdown: { platform: string; value: number; color: string }[];
  pass_types: { type: string; count: number; percentage: number }[];
  mom_change: MoMChange | null;
  yoy_change: YoYChange | null;
  growth_rate: number;
}

const AnalyticsDashboard: React.FC = () => {
  const theme = useTheme();
  const { isMobile, isTablet, getTouchFriendlySize, getSpacing } = useResponsive();

  // Helper: Create analytics dashboard styles
  const createAnalyticsDashboardStyles = () => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      .MuiInputLabel-root, .MuiInputLabel-formControl, .MuiInputLabel-shrink, .MuiFormLabel-root {
        text-decoration: none !important;
      }
      html, body {
        scrollbar-gutter: stable !important;
        overflow-x: hidden !important;
        width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      body.MuiModal-open, body.MuiPopover-open, body.MuiBackdrop-open {
        padding-right: 0 !important;
        overflow: visible !important;
        width: 100% !important;
      }
      .MuiModal-root .MuiBackdrop-root {
        position: fixed !important;
      }
      [data-testid="analytics-container"], .analytics-dashboard-container {
        width: 100% !important;
        overflow-x: hidden !important;
        scrollbar-gutter: stable !important;
      }
    `;
    return styleEl;
  };

  // Global style to remove text decoration and fix scrollbar shift
  React.useEffect(() => {
    const styleEl = createAnalyticsDashboardStyles();
    document.head.appendChild(styleEl);
    return () => { styleEl.remove(); };
  }, []);

  // Component state
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Date range filter states
  const [dateRange, setDateRange] = useState<{start: Date | null, end: Date | null}>({
    start: null,
    end: null
  });
  const [quickDateRange, setQuickDateRange] = useState<string>('custom');

  // Advanced filtering states
  const [passTypeFilter, setPassTypeFilter] = useState<string[]>(['all']);
  const [platformFilter, setPlatformFilter] = useState<string[]>(['all']);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Ref to prevent double initialization
  const hasInitialized = React.useRef(false);
  const isRefreshing = React.useRef(false); // Prevent concurrent refreshes

  const fetchData = useCallback(async () => {
    // Prevent concurrent refresh calls
    if (isRefreshing.current) {
      logger.log('Refresh already in progress, skipping...');
      return;
    }

    isRefreshing.current = true;
    setRefreshing(true);
    setError(null); // Clear previous errors

    // Fetch real data from PassKit API
    try {
      // Build API URL with query parameters
      const params = new URLSearchParams();
      if (dateRange.start && dateRange.end) {
        params.append('start_date', dateRange.start.toISOString().split('T')[0]);
        params.append('end_date', dateRange.end.toISOString().split('T')[0]);
      }
      const url = `/analytics/overview${params.toString() ? '?' + params.toString() : ''}`;
      logger.log('Fetching analytics from:', url);

      // Fetch analytics data from API
      const response = await api.get(url);

      const apiData = response.data;

        logger.log('=== API DATA DEBUG ===');
        logger.log('Full API Response:', apiData);
        logger.log('Total passes:', apiData.total_passes);
        logger.log('Membership count:', apiData.membership_count);
        logger.log('Coupon count:', apiData.coupon_count);
        logger.log('Chart data length:', apiData.chart_data?.length);
        logger.log('Chart data sample:', apiData.chart_data?.slice(-5));

        // Use real chart data from PassKit API
        let allChartData = apiData.chart_data && apiData.chart_data.length > 0
          ? apiData.chart_data.map((d: any) => ({
                date: d.date,
                passes: d.created || 0,
                downloads: d.installed || 0,
                active_users: Math.max(0, (d.created || 0) - (d.invalidated || 0)),
                apple_wallet: 0,
                google_pay: 0
              }))
          : [];

        // Transform API data to match frontend interface
        let rawData: AnalyticsData = {
          total_passes: apiData.total_passes || 0,
          active_passes: apiData.active_passes || 0,
          total_downloads: apiData.total_redemptions || 0,
          apple_wallet: apiData.apple_wallet || 0,
          google_pay: apiData.google_wallet || 0,
          invalidated_passes: apiData.invalidated_passes || 0,
          deleted_passes: apiData.deleted_passes || 0,
          monthly_growth: 0,
          daily_active_users: apiData.active_passes || 0,
          conversion_rate: apiData.redemption_rate || 0,
          chart_data: allChartData,
          platform_breakdown: [
            { platform: 'Apple Wallet', value: apiData.apple_wallet || 0, color: '#007AFF' },
            { platform: 'Google Pay', value: apiData.google_wallet || 0, color: '#4285F4' }
          ],
          pass_types: [
            {
              type: 'Članske karte',
              count: apiData.membership_count || 0,
              percentage: apiData.total_passes > 0 ? ((apiData.membership_count || 0) / apiData.total_passes * 100) : 0
            },
            {
              type: 'Kuponi',
              count: apiData.coupon_count || 0,
              percentage: apiData.total_passes > 0 ? ((apiData.coupon_count || 0) / apiData.total_passes * 100) : 0
            }
          ],
          mom_change: apiData.mom_change || null,
          yoy_change: apiData.yoy_change || null,
          growth_rate: apiData.growth_rate || 0
        };

        logger.log('=== TRANSFORMED DATA ===');
        logger.log('rawData.chart_data length:', rawData.chart_data.length);
        logger.log('rawData.pass_types:', rawData.pass_types);
        logger.log('rawData.total_passes:', rawData.total_passes);

        // Apply filters locally
        let filteredData = { ...rawData };

        // Apply pass type filter
        if (!passTypeFilter.includes('all')) {
          const typeMultiplier = passTypeFilter.length / 4; // 4 total types
          filteredData.total_passes = Math.floor(filteredData.total_passes * typeMultiplier);
          filteredData.active_passes = Math.floor(filteredData.active_passes * typeMultiplier);

          // Filter pass types breakdown
          filteredData.pass_types = filteredData.pass_types.filter(type =>
            passTypeFilter.some(filter =>
              (filter === 'membership' && type.type.includes('Članske')) ||
              (filter === 'coupons' && type.type.includes('Kuponi')) ||
              (filter === 'events' && type.type.includes('Event')) ||
              (filter === 'loyalty' && type.type.includes('Loyalty'))
            )
          );
        }

        // Apply platform filter
        if (!platformFilter.includes('all')) {
          const platformMultiplier = platformFilter.length / 2; // Apple + Google
          filteredData.total_downloads = Math.floor(filteredData.total_downloads * platformMultiplier);

          // Filter platform breakdown
          filteredData.platform_breakdown = filteredData.platform_breakdown.filter(platform =>
            platformFilter.some(filter =>
              (filter === 'apple' && platform.platform.includes('Apple')) ||
              (filter === 'google' && platform.platform.includes('Google'))
            )
          );
        }

        // Apply status filter (simulate effect)
        if (statusFilter !== 'all') {
          let statusMultiplier = 0.1; // expired
          if (statusFilter === 'active') statusMultiplier = 0.8;
          else if (statusFilter === 'inactive') statusMultiplier = 0.2;
          filteredData.active_passes = Math.floor(filteredData.total_passes * statusMultiplier);
        }

      setData(filteredData);
      toast.success('Analitika uspešno ažurirana', {
        position: 'bottom-right',
        autoClose: 2000
      });

    } catch (error: any) {
      logger.error('Failed to fetch analytics:', error);
      const errorMessage = error.message || 'Neuspešno učitavanje analitike';
      setError(errorMessage);

      toast.error(
        <Box>
          <Typography variant="body2">Greška pri učitavanju podataka</Typography>
          <Button
            size="small"
            onClick={() => fetchData()}
            sx={{ mt: 1, color: 'white' }}
          >
            Pokušaj ponovo
          </Button>
        </Box>,
        {
          position: 'bottom-right',
          autoClose: 5000
        }
      );

      // Fallback to empty data
      setData({
        total_passes: 0,
        active_passes: 0,
        total_downloads: 0,
        apple_wallet: 0,
        google_pay: 0,
        invalidated_passes: 0,
        deleted_passes: 0,
        monthly_growth: 0,
        daily_active_users: 0,
        conversion_rate: 0,
        chart_data: [],
        platform_breakdown: [],
        pass_types: [],
        mom_change: null,
        yoy_change: null,
        growth_rate: 0
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
      isRefreshing.current = false; // Reset refresh lock
    }
  }, [passTypeFilter, platformFilter, statusFilter, dateRange]); // Include all filter dependencies

  // Manual refresh handler
  const handleRefresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  // Initial data load
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchData();
    }
  }, [fetchData]);

  // Count active filters
  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    if (!passTypeFilter.includes('all') && passTypeFilter.length > 0) count++;
    if (!platformFilter.includes('all') && platformFilter.length > 0) count++;
    if (statusFilter !== 'all') count++;
    if (dateRange.start && dateRange.end) count++;
    return count;
  }, [passTypeFilter, platformFilter, statusFilter, dateRange]);

  const activeFiltersCount = getActiveFiltersCount();

  if (loading && !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const metrics: DashboardMetric[] = [
    {
      title: 'Kreirani Pass-ovi',
      value: data.total_passes.toLocaleString(),
      change: data.mom_change?.total_passes || 0,
      isPositive: (data.mom_change?.total_passes || 0) >= 0,
      icon: <CreditCardIcon />,
      color: 'primary'
    },
    {
      title: 'Instalirani Pass-ovi',
      value: data.active_passes.toLocaleString(),
      change: data.mom_change?.installed_passes || 0,
      isPositive: (data.mom_change?.installed_passes || 0) >= 0,
      icon: <PeopleIcon />,
      color: 'success'
    },
    {
      title: 'Poništeni Pass-ovi',
      value: data.invalidated_passes.toLocaleString(),
      change: 0,
      isPositive: false,
      icon: <TrendingDown />,
      color: 'error'
    },
    {
      title: 'Stopa Instalacije',
      value: `${data.conversion_rate.toFixed(1)}%`,
      change: 0,
      isPositive: data.conversion_rate > 0,
      icon: <SpeedIcon />,
      color: 'success'
    }
  ];

  return (
    <Box
      className="analytics-dashboard-container"
      data-testid="analytics-container"
      sx={{
        p: 3,
        width: '100%',
        minHeight: '100vh',
        boxSizing: 'border-box',
        overflow: 'auto',
        scrollbarGutter: 'stable',
        position: 'relative'
      }}>
      {/* TEST MARKER - Version 2024-11-30 15:30 */}
      {/* Header */}
      <ResponsivePageHeader
        title="Analitika"
        subtitle="Real-time uvidi i sveobuhvatna analitika za vaše digitalne pass-ove"
        icon={<AnalyticsIcon />}
        actions={
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="contained"
            sx={{
              color: 'white !important',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                border: '2px solid white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.5) !important',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.3)'
              },
              minHeight: getTouchFriendlySize(40),
              width: isMobile ? '100%' : 'auto'
            }}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            Osveži
          </Button>
        }
        showUserAvatar={false}
        showDate={false}
        showCompany={false}
      />

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Pokušaj ponovo
            </Button>
          }
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Advanced Filtering Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              <FilterIcon />
              Napredni Filteri
              {activeFiltersCount > 0 && (
                <Chip
                  label={activeFiltersCount}
                  size="small"
                  color="primary"
                  sx={{ minWidth: 24, height: 20 }}
                />
              )}
            </Typography>
            <Button
              size="small"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {showAdvancedFilters ? 'Sakrij' : 'Prikaži'} Filtere
            </Button>
          </Box>



          {/* Advanced Filters - Collapsible */}
          <Collapse in={showAdvancedFilters}>
            <Box>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} mb={2}>
                {/* Pass Type Filter */}
                <FormControl size="small" sx={{
                  minWidth: isMobile ? '100%' : 200,
                  '& .MuiInputLabel-root': {
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important'
                  },
                  '& .MuiInputLabel-formControl': {
                    textDecoration: 'none !important'
                  },
                  '& .MuiInputLabel-shrink': {
                    textDecoration: 'none !important',
                    backgroundColor: '#f8fafc !important',
                    padding: '0 8px !important'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    '& legend': {
                      display: 'none !important'
                    }
                  }
                }}>
                  <InputLabel sx={{
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important',
                    '&:before': { display: 'none !important' },
                    '&:after': { display: 'none !important' },
                    '&.Mui-focused': {
                      backgroundColor: '#f8fafc !important',
                      padding: '0 8px !important'
                    }
                  }}>Tip Pass-a</InputLabel>
                  <Select
                    multiple
                    value={passTypeFilter}
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;

                      // If no values selected, set to "all"
                      if (value.length === 0) {
                        setPassTypeFilter(['all']);
                      }
                      // If "all" is selected, deselect all specific filters
                      else if (value.includes('all') && !passTypeFilter.includes('all')) {
                        setPassTypeFilter(['all']);
                      }
                      // If specific filter is selected and "all" was previously selected, remove "all"
                      else if (!value.includes('all') && passTypeFilter.includes('all')) {
                        setPassTypeFilter(value.filter(v => v !== 'all'));
                      }
                      // If specific filter is selected, remove "all" if it exists
                      else if (value.includes('all') && value.length > 1) {
                        setPassTypeFilter(value.filter(v => v !== 'all'));
                      }
                      // Normal selection
                      else {
                        setPassTypeFilter(value);
                      }
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={(() => {
                              if (value === 'all') return 'Svi tipovi';
                              if (value === 'membership') return 'Članstvo';
                              if (value === 'coupons') return 'Kuponi';
                              if (value === 'events') return 'Događaji';
                              if (value === 'loyalty') return 'Loyalty';
                              return value;
                            })()}
                            size="small"
                            sx={{
                              '& .MuiChip-label': {
                                textDecoration: 'none !important'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    sx={{
                      '& .MuiSelect-select': {
                        textDecoration: 'none !important'
                      },
                      '& .MuiSelect-multiple': {
                        textDecoration: 'none !important'
                      },
                      '& .MuiInputBase-input': {
                        textDecoration: 'none !important'
                      }
                    }}
                  >
                    <MenuItem value="all">Svi tipovi</MenuItem>
                    <MenuItem value="membership">Članstvo</MenuItem>
                    <MenuItem value="coupons">Kuponi</MenuItem>
                    <MenuItem value="events">Događaji</MenuItem>
                    <MenuItem value="loyalty">Loyalty</MenuItem>
                  </Select>
                </FormControl>

                {/* Platform Filter */}
                <FormControl size="small" sx={{
                  minWidth: isMobile ? '100%' : 200,
                  '& .MuiInputLabel-root': {
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important'
                  },
                  '& .MuiInputLabel-formControl': {
                    textDecoration: 'none !important'
                  },
                  '& .MuiInputLabel-shrink': {
                    textDecoration: 'none !important',
                    backgroundColor: '#f8fafc !important',
                    padding: '0 8px !important'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    '& legend': {
                      display: 'none !important'
                    }
                  }
                }}>
                  <InputLabel sx={{
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important',
                    '&:before': { display: 'none !important' },
                    '&:after': { display: 'none !important' },
                    '&.Mui-focused': {
                      backgroundColor: '#f8fafc !important',
                      padding: '0 8px !important'
                    }
                  }}>Platforma</InputLabel>
                  <Select
                    multiple
                    value={platformFilter}
                    onChange={(e) => {
                      const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;

                      // If no values selected, set to "all"
                      if (value.length === 0) {
                        setPlatformFilter(['all']);
                      }
                      // If "all" is selected, deselect all specific filters
                      else if (value.includes('all') && !platformFilter.includes('all')) {
                        setPlatformFilter(['all']);
                      }
                      // If specific filter is selected and "all" was previously selected, remove "all"
                      else if (!value.includes('all') && platformFilter.includes('all')) {
                        setPlatformFilter(value.filter(v => v !== 'all'));
                      }
                      // If specific filter is selected, remove "all" if it exists
                      else if (value.includes('all') && value.length > 1) {
                        setPlatformFilter(value.filter(v => v !== 'all'));
                      }
                      // Normal selection
                      else {
                        setPlatformFilter(value);
                      }
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={(() => {
                              if (value === 'all') return 'Sve platforme';
                              if (value === 'apple') return 'Apple Wallet';
                              if (value === 'google') return 'Google Pay';
                              return value;
                            })()}
                            size="small"
                            sx={{
                              '& .MuiChip-label': {
                                textDecoration: 'none !important'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    sx={{
                      '& .MuiSelect-select': {
                        textDecoration: 'none !important'
                      },
                      '& .MuiSelect-multiple': {
                        textDecoration: 'none !important'
                      },
                      '& .MuiInputBase-input': {
                        textDecoration: 'none !important'
                      }
                    }}
                  >
                    <MenuItem value="all">Sve platforme</MenuItem>
                    <MenuItem value="apple">Apple Wallet</MenuItem>
                    <MenuItem value="google">Google Pay</MenuItem>
                  </Select>
                </FormControl>

                {/* Status Filter */}
                <FormControl size="small" sx={{
                  minWidth: isMobile ? '100%' : 150,
                  '& .MuiInputLabel-root': {
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important'
                  },
                  '& .MuiInputLabel-formControl': {
                    textDecoration: 'none !important'
                  },
                  '& .MuiInputLabel-shrink': {
                    textDecoration: 'none !important',
                    backgroundColor: '#f8fafc !important',
                    padding: '0 8px !important'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    '& legend': {
                      display: 'none !important'
                    }
                  }
                }}>
                  <InputLabel sx={{
                    textDecoration: 'none !important',
                    backgroundColor: 'transparent !important',
                    '&:before': { display: 'none !important' },
                    '&:after': { display: 'none !important' },
                    '&.Mui-focused': {
                      backgroundColor: '#f8fafc !important',
                      padding: '0 8px !important'
                    }
                  }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      '& .MuiSelect-select': {
                        textDecoration: 'none !important'
                      },
                      '& .MuiInputBase-input': {
                        textDecoration: 'none !important'
                      }
                    }}
                  >
                    <MenuItem value="all">Svi statusi</MenuItem>
                    <MenuItem value="active">Aktivni</MenuItem>
                    <MenuItem value="inactive">Neaktivni</MenuItem>
                    <MenuItem value="expired">Istekli</MenuItem>
                  </Select>
                </FormControl>

                {/* Clear Filters Button */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPassTypeFilter(['all']);
                    setPlatformFilter(['all']);
                    setStatusFilter('all');
                    setDateRange({ start: null, end: null });
                  }}
                  sx={{
                    minHeight: getTouchFriendlySize(40),
                    width: isMobile ? '100%' : 'auto'
                  }}
                >
                  Obriši Filtere
                </Button>
              </Stack>

              {/* Date Range Filter */}
              <Box mt={2}>
                <Typography variant="body2" fontWeight={600} mb={1}>
                  Vremenski Period
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={sr}>
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                    {/* Quick Date Range Selector */}
                    <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 200 }}>
                      <InputLabel>Brzi Izbor</InputLabel>
                      <Select
                        value={quickDateRange}
                        label="Brzi Izbor"
                        onChange={(e) => {
                          const value = e.target.value;
                          setQuickDateRange(value);

                          const today = new Date();
                          today.setHours(23, 59, 59, 999);

                          if (value === '7days') {
                            const start = new Date();
                            start.setDate(start.getDate() - 7);
                            start.setHours(0, 0, 0, 0);
                            setDateRange({ start, end: today });
                          } else if (value === '30days') {
                            const start = new Date();
                            start.setDate(start.getDate() - 30);
                            start.setHours(0, 0, 0, 0);
                            setDateRange({ start, end: today });
                          } else if (value === '90days') {
                            const start = new Date();
                            start.setDate(start.getDate() - 90);
                            start.setHours(0, 0, 0, 0);
                            setDateRange({ start, end: today });
                          }
                        }}
                      >
                        <MenuItem value="7days">Poslednjih 7 Dana</MenuItem>
                        <MenuItem value="30days">Poslednjih 30 Dana</MenuItem>
                        <MenuItem value="90days">Poslednjih 90 Dana</MenuItem>
                      </Select>
                    </FormControl>

                    <DatePicker
                      label="Od"
                      value={dateRange.start}
                      onChange={(newValue) => {
                        setDateRange({ ...dateRange, start: newValue as Date | null });
                        setQuickDateRange('custom');
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: isMobile,
                          sx: { minWidth: isMobile ? '100%' : 200 }
                        }
                      }}
                      maxDate={dateRange.end || new Date()}
                    />
                    <DatePicker
                      label="Do"
                      value={dateRange.end}
                      onChange={(newValue) => {
                        setDateRange({ ...dateRange, end: newValue as Date | null });
                        setQuickDateRange('custom');
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: isMobile,
                          sx: { minWidth: isMobile ? '100%' : 200 }
                        }
                      }}
                      minDate={dateRange.start || undefined}
                      maxDate={new Date()}
                    />
                  </Stack>
                </LocalizationProvider>

                {dateRange.start && dateRange.end && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Prikaz od {dateRange.start.toLocaleDateString('sr-RS')} do {dateRange.end.toLocaleDateString('sr-RS')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Metrics Cards using Flexbox */}
      <Box display="flex" flexWrap="wrap" gap={getSpacing(2, 3)} mb={getSpacing(2, 3)}>
        {metrics.map((metric) => {
          let flexValue: string;
          if (isMobile) {
            flexValue = "1 1 100%";
          } else if (isTablet) {
            flexValue = "1 1 45%";
          } else {
            flexValue = "1 1 280px";
          }
          return (
            <Box key={metric.title} flex={flexValue}>
              <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={{ xs: 1.5, md: 2 }}>
                  <Avatar sx={{
                    backgroundColor: alpha(theme.palette[metric.color].main, 0.1),
                    color: theme.palette[metric.color].main,
                    width: { xs: 40, md: 48 },
                    height: { xs: 40, md: 48 }
                  }}>
                    {metric.icon}
                  </Avatar>
                  <Chip
                    label={`${metric.change > 0 ? '+' : ''}${metric.change}%`}
                    color={metric.isPositive ? 'success' : 'error'}
                    size="small"
                    icon={metric.isPositive ? <TrendingUp /> : <TrendingDown />}
                  />
                </Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color={metric.color + '.main'}
                  sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.125rem' } }}
                >
                  {metric.value}
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {metric.title}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          );
        })}
      </Box>

      {/* Growth Rate & Comparative Analytics */}
      {data.mom_change && data.total_passes > 0 && (
        <Box display="flex" flexWrap="wrap" gap={getSpacing(2, 3)} mb={getSpacing(2, 3)}>
          <Box flex="1 1 300px">
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mb={{ xs: 1.5, md: 2 }}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Stopa Rasta (MoM)
                </Typography>
                <Box display="flex" alignItems="center" gap={{ xs: 1.5, md: 2 }}>
                  <Typography
                    variant="h3"
                    color={data.growth_rate >= 0 ? 'success.main' : 'error.main'}
                    fontWeight={700}
                    sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
                  >
                    {data.growth_rate >= 0 ? '+' : ''}{data.growth_rate.toFixed(1)}%
                  </Typography>
                  {data.growth_rate >= 0 ? (
                    <TrendingUp color="success" sx={{ fontSize: { xs: 32, md: 40 } }} />
                  ) : (
                    <TrendingDown color="error" sx={{ fontSize: { xs: 32, md: 40 } }} />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" mt={1} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                  U odnosu na prethodni period
                </Typography>

                {data.yoy_change && (
                  <>
                    <Box sx={{ borderTop: 1, borderColor: 'divider', my: { xs: 1.5, md: 2 } }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      YoY: {data.yoy_change.total_passes >= 0 ? '+' : ''}
                      {data.yoy_change.total_passes.toFixed(1)}%
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>

          <Box flex="1 1 300px">
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mb={{ xs: 1.5, md: 2 }}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Promene po Metrikama
                </Typography>
                <Stack spacing={{ xs: 1.5, md: 2 }}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Kreirani Pass-ovi</Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color={data.mom_change.total_passes >= 0 ? 'success.main' : 'error.main'}
                        sx={{ fontSize: { xs: '0.925rem', md: '1rem' } }}
                      >
                        {data.mom_change.total_passes >= 0 ? '+' : ''}
                        {data.mom_change.total_passes.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Instalirani Pass-ovi</Typography>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        color={data.mom_change.installed_passes >= 0 ? 'success.main' : 'error.main'}
                        sx={{ fontSize: { xs: '0.925rem', md: '1rem' } }}
                      >
                        {data.mom_change.installed_passes >= 0 ? '+' : ''}
                        {data.mom_change.installed_passes.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Charts Row using Flexbox */}
      <Box display="flex" flexWrap="wrap" gap={getSpacing(2, 3)} mb={getSpacing(2, 3)}>
        {/* Main Trend Chart */}
        <Box flex={(() => {
          if (isMobile) return "1 1 100%";
          if (isTablet) return "1 1 100%";
          return "2 1 600px";
        })()}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                fontWeight={600}
                mb={{ xs: 2, md: 3 }}
                sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
              >
                Trend Kreiranja Pass-ova
              </Typography>
              <Box height={{ xs: 220, sm: 250, md: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.chart_data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      style={{ fontSize: isMobile ? '11px' : '12px' }}
                    />
                    <YAxis
                      style={{ fontSize: isMobile ? '11px' : '12px' }}
                    />
                    <RechartsTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="passes"
                      stackId="1"
                      stroke={theme.palette.primary.main}
                      fill={alpha(theme.palette.primary.main, 0.3)}
                      name="Pass-ovi"
                    />
                    <Area
                      type="monotone"
                      dataKey="downloads"
                      stackId="1"
                      stroke={theme.palette.success.main}
                      fill={alpha(theme.palette.success.main, 0.3)}
                      name="Preuzimanja"
                    />
                    <Area
                      type="monotone"
                      dataKey="active_users"
                      stackId="1"
                      stroke={theme.palette.info.main}
                      fill={alpha(theme.palette.info.main, 0.3)}
                      name="Aktivni Korisnici"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Pass Types Breakdown - Show if we have any pass data */}
      {data && data.total_passes > 0 && (
        <Box display="flex" flexWrap="wrap" gap={getSpacing(2, 3)}>
          {/* iOS vs Android Breakdown */}
          <Box flex="1 1 380px">
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mb={{ xs: 2, md: 3 }}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  iOS vs Android
                </Typography>
                <Box height={{ xs: 200, sm: 220, md: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          {
                            name: 'Apple Wallet (iOS)',
                            value: data.apple_wallet,
                            color: '#007AFF'
                          },
                          {
                            name: 'Google Wallet (Android)',
                            value: data.google_pay,
                            color: '#34A853'
                          }
                        ].filter(item => item.value > 0)}
                        cx="50%"
                        cy="50%"
                        outerRadius={isMobile ? 70 : 95}
                        dataKey="value"
                        label={(entry: any) => {
                          const percent = entry.percent as number;
                          return `${(percent * 100).toFixed(1)}%`;
                        }}
                      >
                        {[
                          { name: 'Apple Wallet (iOS)', value: data.apple_wallet, color: '#007AFF' },
                          { name: 'Google Wallet (Android)', value: data.google_pay, color: '#34A853' }
                        ].filter(item => item.value > 0).map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value: number | undefined) => [value?.toLocaleString() || '0', 'Instalacija']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Stack spacing={{ xs: 1, md: 1 }} mt={{ xs: 1.5, md: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        sx={{ backgroundColor: '#007AFF' }}
                      />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Apple Wallet (iOS)</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      {data.apple_wallet.toLocaleString()}
                      <Typography component="span" variant="caption" color="text.secondary" ml={1} sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                        ({((data.apple_wallet / (data.apple_wallet + data.google_pay)) * 100).toFixed(1)}%)
                      </Typography>
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        width={12}
                        height={12}
                        borderRadius="50%"
                        sx={{ backgroundColor: '#34A853' }}
                      />
                      <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>Google Wallet (Android)</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}>
                      {data.google_pay.toLocaleString()}
                      <Typography component="span" variant="caption" color="text.secondary" ml={1} sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
                        ({((data.google_pay / (data.apple_wallet + data.google_pay)) * 100).toFixed(1)}%)
                      </Typography>
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          {/* Pass Types as Pie Chart */}
          <Box flex="1 1 450px">
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  mb={{ xs: 2, md: 3 }}
                  sx={{ fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}
                >
                  Tipovi Pass-ova
                </Typography>
                <Box height={{ xs: 200, sm: 220, md: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.pass_types.filter(t => t.count > 0).map(type => ({
                          name: type.type,
                          value: type.count,
                          percentage: type.percentage
                        }))}
                        cx="50%"
                        cy="50%"
                        outerRadius={isMobile ? 70 : 95}
                        dataKey="value"
                        label={(entry: any) => {
                          const name = entry.name as string;
                          const percent = entry.percentage as number;
                          return isMobile ? `${percent.toFixed(0)}%` : `${name}: ${percent.toFixed(1)}%`;
                        }}
                      >
                        {data.pass_types.filter(t => t.count > 0).map((type, index) => (
                          <Cell
                            key={type.type}
                            fill={['#14264a', '#dc004e', '#9c27b0', '#f57c00'][index % 4]}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value: number | undefined) => value?.toLocaleString() || '0'}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Stack spacing={1} mt={2}>
                  {data.pass_types.filter(t => t.count > 0).map((type, index) => (
                    <Box key={type.type} display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          width={12}
                          height={12}
                          borderRadius="50%"
                          sx={{ backgroundColor: ['#14264a', '#dc004e', '#9c27b0', '#f57c00'][index % 4] }}
                        />
                        <Typography variant="body2">{type.type}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        {type.count.toLocaleString()}
                        <Typography component="span" variant="caption" color="text.secondary" ml={1}>
                          ({type.percentage.toFixed(1)}%)
                        </Typography>
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Export Analytics Section */}
      <Box mt={3}>
        <ExportAnalytics dateRange={dateRange} />
      </Box>

      {/* Toast Container */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </Box>
  );
};

export default AnalyticsDashboard;
